/**
 * Skills API client for ros-app.
 *
 * Mirrors packages/web/src/services/SkillsService.ts but uses ros-app's
 * auth pattern (Bearer token + credentials:include) instead of the web's
 * apiFetch wrapper. Configure with apiBaseUrl + token from useRosShell()
 * in the render body, exactly like AgentBuilderService.
 *
 * No yaml/jszip dependency — uses an inline frontmatter builder.
 */

export interface SkillSummary {
  id: string
  name: string
  description: string
  enabled: boolean
  createdAt: number
  updatedAt: number
  lastUsedAt: number | null
}

export interface SkillDetail extends SkillSummary {
  instructions: string
  references: string[]
}

export interface SkillFormData {
  name: string
  description: string
  instructions: string
  references?: string[]
}

export interface UpdateSkillInput {
  name?: string
  description?: string
  instructions?: string
  enabled?: boolean
}

export class SkillRequestError extends Error {
  readonly details: string[]
  readonly status: number
  constructor(message: string, status: number, details: string[] = []) {
    super(message)
    this.name = 'SkillRequestError'
    this.status = status
    this.details = details
  }
}

/** Builds SKILL.md markdown from form data without an external yaml dep. */
export function buildSkillMarkdown(form: SkillFormData): string {
  let front = `name: ${form.name.trim()}\n`
  front += `description: "${form.description.trim().replace(/"/g, '\\"')}"\n`
  const refs = (form.references ?? []).map(r => r.trim()).filter(Boolean)
  if (refs.length > 0) {
    front += `references:\n${refs.map(r => `  - ${r}`).join('\n')}\n`
  }
  return `---\n${front}---\n\n${form.instructions.trim()}\n`
}

class SkillsService {
  private baseUrl = ''
  private token: string | null = null

  configure(baseUrl: string, token: string | null): void {
    this.baseUrl = baseUrl
    this.token = token
  }

  private headers(contentType = 'application/json'): Record<string, string> {
    const h: Record<string, string> = { 'Content-Type': contentType }
    if (this.token) h['Authorization'] = `Bearer ${this.token}`
    return h
  }

  private async parseError(response: Response): Promise<never> {
    const body = await response.json().catch(() => ({ error: 'Request failed' }))
    const details = Array.isArray(body.details) ? body.details : []
    throw new SkillRequestError(
      body.error || `Request failed: ${response.status}`,
      response.status,
      details
    )
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      credentials: 'include',
      headers: this.headers(),
      ...init,
    })
    if (!response.ok) await this.parseError(response)
    return response.json() as Promise<T>
  }

  async listSkills(): Promise<SkillSummary[]> {
    return this.request<SkillSummary[]>('/api/skills')
  }

  async getSkill(id: string): Promise<SkillDetail> {
    return this.request<SkillDetail>(`/api/skills/${encodeURIComponent(id)}`)
  }

  async createSkill(form: SkillFormData): Promise<{ id: string; name: string }> {
    const response = await fetch(`${this.baseUrl}/api/skills`, {
      credentials: 'include',
      method: 'POST',
      headers: { ...this.headers('text/markdown') },
      body: buildSkillMarkdown(form),
    })
    if (!response.ok) await this.parseError(response)
    return response.json() as Promise<{ id: string; name: string }>
  }

  async importSkill(file: File): Promise<{ id: string; name: string }> {
    const response = await fetch(`${this.baseUrl}/api/skills/import`, {
      credentials: 'include',
      method: 'POST',
      headers: { ...this.headers('application/octet-stream') },
      body: file,
    })
    if (!response.ok) await this.parseError(response)
    return response.json() as Promise<{ id: string; name: string }>
  }

  async updateSkill(id: string, input: UpdateSkillInput): Promise<SkillDetail> {
    return this.request<SkillDetail>(`/api/skills/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
  }

  async deleteSkill(id: string): Promise<void> {
    await this.request(`/api/skills/${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  async duplicateSkill(id: string, name: string): Promise<{ id: string; name: string }> {
    return this.request<{ id: string; name: string }>(
      `/api/skills/${encodeURIComponent(id)}/duplicate`,
      { method: 'POST', body: JSON.stringify({ name }) }
    )
  }

  async getReferenceBlob(id: string, fileName: string): Promise<Blob> {
    const response = await fetch(
      `${this.baseUrl}/api/skills/${encodeURIComponent(id)}/references/${encodeURIComponent(fileName)}`,
      { credentials: 'include', headers: this.token ? { Authorization: `Bearer ${this.token}` } : {} }
    )
    if (!response.ok) await this.parseError(response)
    return response.blob()
  }

  async uploadReference(id: string, file: File): Promise<{ fileName: string; sizeBytes: number }> {
    const response = await fetch(
      `${this.baseUrl}/api/skills/${encodeURIComponent(id)}/references?fileName=${encodeURIComponent(file.name)}`,
      {
        credentials: 'include',
        method: 'POST',
        headers: {
          ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      }
    )
    if (!response.ok) await this.parseError(response)
    return response.json() as Promise<{ fileName: string; sizeBytes: number }>
  }

  async deleteReference(id: string, fileName: string): Promise<void> {
    await this.request(
      `/api/skills/${encodeURIComponent(id)}/references/${encodeURIComponent(fileName)}`,
      { method: 'DELETE' }
    )
  }
}

const skillsService = new SkillsService()
export default skillsService
